import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AppConfig, appConfig as appConfigManager } from '../config/appConfig';

interface AppConfigContextType {
  config: AppConfig;
  updateConfig: (updates: Partial<AppConfig>) => void;
  resetConfig: () => void;
  isLoading: boolean;
}

const AppConfigContext = createContext<AppConfigContextType | undefined>(undefined);

export const AppConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(appConfigManager.getConfig());
  const [isLoading, setIsLoading] = useState(false);

  // Load config on mount
  useEffect(() => {
    const loadedConfig = appConfigManager.getConfig();
    setConfig(loadedConfig);
  }, []);

  const updateConfig = (updates: Partial<AppConfig>) => {
    setIsLoading(true);
    try {
      appConfigManager.updateConfig(updates);
      setConfig(appConfigManager.getConfig());
    } finally {
      setIsLoading(false);
    }
  };

  const resetConfig = () => {
    setIsLoading(true);
    try {
      appConfigManager.resetConfig();
      setConfig(appConfigManager.getConfig());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppConfigContext.Provider value={{ config, updateConfig, resetConfig, isLoading }}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = (): AppConfigContextType => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
};
